import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ProfileActions from "../actions/ProfileActions";
import ProfileStore from "../stores/ProfileStore";
import SearchStore from "../stores/SearchStore";
import Riskified from "arbitrip-common/client/utils/Riskified";
import Analytics from 'arbitrip-common/client/analytics';
import { CLIENT_ROUTES } from 'arbitrip-common/general/constants';
import i18n from 'arbitrip-common/translation/desktop.js';
import { consumePendingSearch } from '../utils/searchResultsLanding';
import { createPageViewController, getPageNavigationKey, getPageViewContext } from 'arbitrip-common/client/analytics/services/pageViewContextService';

const RESERVATION_FLOW_ROUTES = [CLIENT_ROUTES.RESULTS, CLIENT_ROUTES.HOTEL, CLIENT_ROUTES.RESERVATION];

const getRootRoute = (pathname) => pathname.split('/').filter(Boolean)[0];

const NavigationTracker = () => {
    const location = useLocation();
    const lastViewedNavigationRef = useRef(null);

    useEffect(() => {
        const navigationKey = getPageNavigationKey(location);
        if (lastViewedNavigationRef.current === navigationKey) {
            return;
        }
        const controller = createPageViewController(
            () => getPageViewContext({
                location,
                isReservationFlow: RESERVATION_FLOW_ROUTES.includes(getRootRoute(location.pathname)),
                storedSearch: SearchStore.getSearchTerms(),
                profileReady: ProfileStore.isServerProfileLoaded(),
            }),
            ({ search, destination }) => {
                lastViewedNavigationRef.current = navigationKey;
                try {
                    Analytics.actions.views.pageViewed(ProfileStore.getProfile(), {
                        pathname: location.pathname,
                        search,
                        destination,
                        language: i18n.language,
                    });
                } catch (_err) {
                    // intentionally ignored
                }
            },
        );
        SearchStore.addChangeListener(controller.update);
        ProfileStore.addChangeListener(controller.update);
        return () => {
            SearchStore.removeChangeListener(controller.update);
            ProfileStore.removeChangeListener(controller.update);
            controller.dispose();
        };
    }, [ location.pathname, location.search ]);

    useEffect(() => {
        // Send navigation event to the backend
        ProfileActions.logNavigation(ProfileStore.getProfile());
        Riskified.trackPageview(location.pathname);

        // Consumed on every navigation so a stale Search click never fires on a later back/deep-link landing.
        const pendingSearch = consumePendingSearch();
        if (pendingSearch && getRootRoute(location.pathname) === CLIENT_ROUTES.RESULTS) {
            Analytics.actions.interactions.searchResultsLanded(
                pendingSearch.user,
                pendingSearch.destination,
                pendingSearch.search,
            );
        }
    }, [ location ]);

    return null; // This component does not render anything
};

export default NavigationTracker;
